<template>
  <div class="page-container">
    <PageHeader icon="stations-list-active.svg" title="Список АЗС" />
    <div class="page-content">
      <div class="tab-content table">
        <TableComponent
          :noData="noData"
          :columns="columns"
          :rows="pageRows"
          :totalCount="totalCount"
          :pageSize="pageSize"
          :pageCount="pageCount"
          :currentPage="Number(currentPage)"
          :isLoading="loading"
          :checkingOn="true"
          :title="'АЗС'"
          :isBordered="true"
          :show-additional-table-header="false"
          :isLightFontAdditional="true"
          :actionType="'stationsList'"
          :batchFranchiseEnabled="batchActionEnabled"
          :batchFranchiseLabel="batchActionLabel"
          :batchFranchiseMark="batchTargetFranchise"
          :franchiseUpdating="updating"
          @franchiseAction="onFranchiseAction"
          @regetData="regetData($event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapMutations, mapState } from 'vuex'

  function parseRawResponse(rawResponse) {
    if (rawResponse && typeof rawResponse === 'object' && !Array.isArray(rawResponse)) {
      return rawResponse
    }
    if (typeof rawResponse === 'string') {
      const trimmed = rawResponse.trim()
      if (!trimmed) return {}
      try {
        let parsed = JSON.parse(trimmed)
        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed.trim())
          } catch (e) {
            return {}
          }
        }
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed
        }
      } catch (e) {
        return {}
      }
    }
    return {}
  }

  function parseApiCoord(value) {
    if (value == null || value === '') return null
    const n = Number(String(value).trim())
    return Number.isFinite(n) ? n : null
  }

  function looksLikeStationNumberLabel(value) {
    const s = String(value ?? '').trim()
    if (!s) return false
    if (/АЗС|№/i.test(s)) return true
    return /^\d[\d\-/]*$/.test(s)
  }

  /** Как в FuelPlanner: station_number сверху → raw_response → fallback id */
  function resolveStationNumber(raw, item) {
    const topLevel = item?.station_number ?? item?.number ?? item?.stationNumber
    if (topLevel != null && String(topLevel).trim() !== '') {
      return String(topLevel).trim()
    }

    if (raw && typeof raw === 'object') {
      const n = String(raw.n ?? '').trim()
      if (n) return n

      if (raw.number != null && String(raw.number).trim() !== '') {
        if (typeof raw.number === 'number' && Number.isFinite(raw.number)) {
          return `АЗС № ${raw.number}`
        }
        const numStr = String(raw.number).trim()
        if (/АЗС|№/i.test(numStr)) return numStr
        return `АЗС № ${numStr}`
      }

      const caption = String(raw.caption ?? '').trim()
      if (caption) return caption

      const fuelShortcode = String(raw.fuelShortcode ?? '').trim()
      if (fuelShortcode) return fuelShortcode

      const name = String(raw.name ?? '').trim()
      if (name && looksLikeStationNumberLabel(name)) return name

      if (raw.kod != null && String(raw.kod).trim() !== '') {
        return `АЗС № ${String(raw.kod).trim()}`
      }
    }

    const fallbackId = item?.station_id ?? item?.id
    return fallbackId != null ? `АЗС № ${fallbackId}` : '—'
  }

  function resolveStationBrand(item) {
    return (
      String(item?.brand ?? '').trim() ||
      String(item?.source ?? '').trim() ||
      'Без бренда'
    )
  }

  function toFranchiseBool(value) {
    return value === true || value === 1 || value === '1'
  }

  /** id для POST stations_ids — бизнес station_id, не PK записи координат */
  function toApiStationId(row) {
    const raw = row?.station_id != null ? row.station_id : row?.id
    if (raw == null || raw === '') return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : raw
  }

  function mapStationRow(item) {
    if (!item || typeof item !== 'object') return null
    const coordinateId = item.id != null ? item.id : null
    const stationId = item.station_id != null ? item.station_id : null
    // Для строки нужен стабильный ключ; в API уходит station_id (см. toApiStationId)
    const id = coordinateId != null ? coordinateId : stationId
    if (id == null) return null

    const raw = parseRawResponse(item.raw_response)
    let lat = parseApiCoord(item.latitude)
    let lng = parseApiCoord(item.longitude)
    if (lat == null) lat = parseApiCoord(raw.y)
    if (lng == null) lng = parseApiCoord(raw.x)

    const coordsLabel =
      lat != null && lng != null ? `${lat}, ${lng}` : '—'
    const address =
      String(raw.a || raw.address || raw.fuelAdress || '').trim() || '—'
    const is_franchise = toFranchiseBool(item.is_franchise)

    return {
      id,
      station_id: stationId != null ? stationId : coordinateId,
      brand: resolveStationBrand(item),
      stationNumber: resolveStationNumber(raw, item),
      address,
      coordsLabel,
      is_franchise,
      franchise_label: is_franchise ? 'Да' : 'Нет',
      green: is_franchise ? { franchise_label: true } : undefined,
      checked: false,
      actionType: 'stationsList'
    }
  }

  export default {
    name: 'StationsList',
    data() {
      return {
        tableColumns: [
          {
            name: 'Бренд',
            field: 'brand',
            filter_name: 'brand',
            filter_value: '',
            order: ''
          },
          {
            name: 'Номер АЗС',
            field: 'stationNumber',
            filter_name: 'stationNumber',
            filter_value: '',
            order: ''
          },
          {
            name: 'Адрес',
            field: 'address',
            filter_name: 'address',
            filter_value: '',
            order: ''
          },
          {
            name: 'Координаты',
            field: 'coordsLabel',
            no_filter: true
          },
          {
            name: 'Франшиза',
            field: 'franchise_label',
            no_filter: true
          },
          {
            name: '',
            field: 'actions',
            no_filter: true
          }
        ],
        stations: [],
        noData: false,
        loading: false,
        updating: false,
        currentPage: 1,
        pageSize: 100
      }
    },
    computed: {
      ...mapState({
        stationCoordinates: (state) => state.references.stationCoordinates,
        columns: (state) => state.popups.columns
      }),
      filterSignature() {
        if (!Array.isArray(this.columns)) return ''
        return this.columns
          .map((c) => `${c.filter_name}:${c.filter_value ?? ''}:${c.order ?? ''}`)
          .join('|')
      },
      filteredStations() {
        let rows = Array.isArray(this.stations) ? [...this.stations] : []
        const cols = Array.isArray(this.columns) ? this.columns : []

        for (const col of cols) {
          const raw = col.filter_value
          if (raw == null || raw === '') continue

          const value =
            typeof raw === 'object' ? String(raw.id ?? raw.name ?? '').trim() : String(raw).trim()
          if (!value) continue

          if (col.filter_name === 'brand') {
            rows = rows.filter(
              (r) => r.brand === value || String(r.brand ?? '').includes(value)
            )
          } else if (col.filter_name === 'stationNumber') {
            const needle = value.toLowerCase()
            rows = rows.filter((r) =>
              String(r.stationNumber ?? '')
                .toLowerCase()
                .includes(needle)
            )
          } else if (col.filter_name === 'address') {
            const needle = value.toLowerCase()
            rows = rows.filter((r) =>
              String(r.address ?? '')
                .toLowerCase()
                .includes(needle)
            )
          }
        }

        const sortCol = cols.find((c) => c.order === 'asc' || c.order === 'desc')
        if (sortCol?.field) {
          const field = sortCol.field
          const desc = sortCol.order === 'desc'
          rows.sort((a, b) => {
            const av = String(a[field] ?? '')
            const bv = String(b[field] ?? '')
            if (av === bv) return 0
            if (desc) return av > bv ? -1 : 1
            return av > bv ? 1 : -1
          })
        } else {
          rows.sort((a, b) => Number(a.id) - Number(b.id))
        }

        return rows
      },
      totalCount() {
        return this.filteredStations.length
      },
      pageCount() {
        if (!this.totalCount || !this.pageSize) return 0
        return Math.ceil(this.totalCount / Number(this.pageSize))
      },
      pageRows() {
        const size = Number(this.pageSize) || 50
        const page = Number(this.currentPage) || 1
        const start = (page - 1) * size
        return this.filteredStations.slice(start, start + size)
      },
      selectedRows() {
        return this.stations.filter((s) => s.checked)
      },
      batchFranchiseUniform() {
        const selected = this.selectedRows
        if (!selected.length) return null
        const first = selected[0].is_franchise
        return selected.every((s) => s.is_franchise === first) ? first : null
      },
      batchActionEnabled() {
        return this.batchFranchiseUniform !== null
      },
      batchTargetFranchise() {
        if (this.batchFranchiseUniform === null) return true
        return !this.batchFranchiseUniform
      },
      batchActionLabel() {
        return this.batchTargetFranchise
          ? 'Пометить как франшизы'
          : 'Убрать пометку франшиз'
      }
    },
    watch: {
      filterSignature() {
        this.currentPage = 1
      }
    },
    async mounted() {
      this.setColumns(null)
      this.setColumns_2(null)
      await this.loadStations()
    },
    methods: {
      ...mapActions({
        getStationCoordinatesAll: 'getStationCoordinatesAll',
        updateStationFranchise: 'updateStationFranchise'
      }),
      ...mapMutations({
        setColumns: 'SET_COLUMNS',
        setColumns_2: 'SET_COLUMNS_2'
      }),
      mapList(list) {
        const rows = []
        for (const item of Array.isArray(list) ? list : []) {
          const row = mapStationRow(item)
          if (row) rows.push(row)
        }
        return rows
      },
      regetData(data) {
        const size = Number(data?.size) || this.pageSize || 50
        let pageNumber = Number(data?.pageNumber) || 1
        if (Math.ceil(this.totalCount / size) < pageNumber) {
          pageNumber = 1
        }
        this.pageSize = size
        this.currentPage = pageNumber
      },
      async loadStations() {
        this.loading = true
        try {
          const res = await this.getStationCoordinatesAll()
          const list = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(this.stationCoordinates)
              ? this.stationCoordinates
              : []
          this.stations = this.mapList(list)
          if (this.currentPage > this.pageCount && this.pageCount > 0) {
            this.currentPage = this.pageCount
          }
          this.noData = !this.stations.length
          this.setColumns(this.tableColumns)
        } catch (_) {
          this.stations = []
          this.noData = true
          this.setColumns(this.tableColumns)
        } finally {
          this.loading = false
        }
      },
      async applyFranchise(ids, isFranchise) {
        const stations_ids = (Array.isArray(ids) ? ids : [])
          .map((id) => (id != null && typeof id === 'object' ? toApiStationId(id) : id))
          .map((id) => {
            if (id == null || id === '') return null
            const n = Number(id)
            return Number.isFinite(n) ? n : id
          })
          .filter((id) => id != null)
        if (!stations_ids.length || this.updating) return
        this.updating = true
        try {
          await this.updateStationFranchise({
            stations_ids,
            is_franchise: isFranchise
          })
          await this.loadStations()
        } catch (_) {
          // showError уже из Vuex action
        } finally {
          this.updating = false
        }
      },
      onFranchiseAction(payload) {
        if (!payload || this.updating) return
        if (payload.type === 'batch') {
          if (!this.batchActionEnabled) return
          const ids = this.selectedRows
            .map((r) => toApiStationId(r))
            .filter((id) => id != null)
          this.applyFranchise(ids, this.batchTargetFranchise)
          return
        }
        if (payload.type === 'row' && payload.row) {
          const id = toApiStationId(payload.row)
          if (id == null) return
          this.applyFranchise([id], !payload.row.is_franchise)
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .tab-content {
    margin-top: 30px;
  }
</style>
